import {defineStore} from 'pinia';
import i18n from '@/i18n';
import {computed, nextTick, ref, watch} from 'vue';
import {useAuthApi} from "@store/api/authApi";
import {useUserApi} from "@store/api/userApi";
import {LoginInfo, User, UserUpdate} from "@store/api/interfaces";
import {ApiAuthError} from "@composables/useApi";
import { UserOtpVerifyInterface } from '@/interfaces/user-login.interface';
import * as Sentry from '@sentry/browser';
import { useHelpers } from '@/composables/helpers';
import { gtmLogin, gtmSignUp, gtmUserId, type GtmLoginMethod } from '@/composables/gtm';

export const useUserStore = defineStore('user', () => {
	const user = ref<User>({lang: localStorage.getItem("user-locale") || 'ru'} as User);
	const authApi = useAuthApi();
	const userApi = useUserApi();
	const helpers = useHelpers();

	const loggedIn = computed(():boolean => {
		return !!user.value?.id && !!authApi.methods.getToken();
	});

	const authMiniApp = async (initRaw: string): Promise<void> => {
		Sentry.addBreadcrumb({
			category: 'miniapp.auth',
			level: 'info',
			message: 'aMiniApp starting',
		});

		// Only a login that actually happened here can be a registration; an
		// already-present token means this session was restored, not created.
		let loginInfo: LoginInfo | null = null;
		if (!authApi.methods.getToken()) {
			loginInfo = await authApi.methods.loginWithMiniApp(initRaw);
		}

		await nextTick();

		if (!authApi.methods.getToken()) {
			throw new ApiAuthError({
				success: false,
				data: {},
				message: 'MiniApp ath failed',
			});
		} else {
			Sentry.addBreadcrumb({
				category: 'miniapp.auth',
				message: 'aMiniApp get tn',
				level: 'info',
				data: {
					tn_type: helpers.getType(authApi.token),
					tn_length: authApi.token?.length,
				}
			});
		}

		const responseUser = await userApi.get();
		await methods.setup(responseUser, 'telegram', loginInfo?.is_new_user);
	}
	const isAuthError = (e: any) =>
		e instanceof ApiAuthError ||
		e?.status === 401 ||
		e?.response?.status === 401 ||
		e?.name === 'ApiAuthError';

	const methods = {
		tgMiniAppInit: async (raw: string) => {
			try {
				await authMiniApp(raw);

				Sentry.addBreadcrumb({
					category: 'miniapp.auth',
					level: 'info',
					message: 'tgMiniAppInit succeeded',
					data: {
						attempt: 1,
					},
				});
			} catch (e) {
				if (isAuthError(e)) {
					Sentry.captureException(e, {
						tags: {
							stage: 'tgMiniAppInit',
							attempt: '1',
							retry: 'true',
						},
					});

					authApi.methods.clearToken();
					await authMiniApp(raw);

					Sentry.addBreadcrumb({
						category: 'miniapp.auth',
						level: 'info',
						message: 'tgMiniAppInit succeeded after retry',
						data: {
							attempt: 2,
						},
					});
				} else {
					Sentry.captureException(e, {
						tags: {
							stage: 'tgMiniAppInit',
							fatal: 'true',
						},
					});
					throw e;
				}
			}
		},
		loginByData: async(authData: LoginInfo, loginMethod: GtmLoginMethod) => {
			const { user, token } = authData;
			authApi.methods.saveToken(token);
			await methods.setup(user, loginMethod, authData.is_new_user);
		},
		login: async(data: UserOtpVerifyInterface) => {
			const response = await authApi.methods.loginWithOtp(data.email, data.code, data.lang);
			await methods.setup(response.user, 'email', response.is_new_user);
			return response;
		},
		/**
		 * Single entry point for every way in (MiniApp, email OTP, social
		 * providers incl. the OAuth return, cross-domain handoff) — which is
		 * why the registration event lives here rather than per provider.
		 *
		 * `isNewUser` comes from the auth response. While the API omits it the
		 * client cannot tell a first login from a returning one, so everyone
		 * keeps counting as `login` and `sign_up` simply stays silent, exactly
		 * as before.
		 */
		setup: async(responseUser: User, loginMethod: GtmLoginMethod, isNewUser?: boolean) => {
			methods.refresh(responseUser);
			if (!responseUser?.id) return;

			// Before the login/sign_up event, not after: it lets GTM re-run its
			// Google tag with the id, so the auth event itself already carries
			// User-ID instead of being the last one without it.
			gtmUserId(responseUser.id);

			const isRegistration = isNewUser ?? responseUser.is_new_user;
			if (!isRegistration) {
				gtmLogin(responseUser.id, loginMethod);
				return;
			}

			gtmSignUp(responseUser.id, loginMethod);
		},
		// Update the stored user WITHOUT emitting a GTM login event — for
		// non-auth updates (policy accept, onboarding) that return a fresh user.
		refresh: (responseUser: User) => {
			user.value = responseUser;
			if (responseUser?.id) {
				Sentry.setUser({ id: String(responseUser.id) });
			}
		},
		// todo: rename to fetch
		set: async () => {
			user.value = await userApi.get();
			if (user.value?.id) {
				gtmUserId(user.value.id);
				Sentry.setUser({ id: String(user.value.id) });
			}
		},
		update: async (params: UserUpdate, withoutLocalSave: boolean = false): Promise<User> => {
			if (!withoutLocalSave) {
				user.value = await userApi.update(params);
			}
			return user.value;
		}
	}

	const updateLocalUserLanguage = (locale: string) => {
		localStorage.setItem("user-locale", locale);
		i18n.global.locale.value = (locale as "ru" || "en" || "es") || 'ru';
		document.querySelector("html")?.setAttribute("lang", locale);
	}

	const isUpdatingLanguage = ref(false);

	watch(
		() => user?.value?.lang,
		async (locale) => {
			updateLocalUserLanguage(locale || 'ru');
			if (loggedIn.value) {
				isUpdatingLanguage.value = true;
				try {
					await userApi.update({lang: locale});
				} finally {
					isUpdatingLanguage.value = false;
				}
			}
		}
	);

	return {
		user,
		loggedIn,
		isUpdatingLanguage,
		methods,
		updateUserLanguage: updateLocalUserLanguage
	};
})
